export interface EvidenceReference {
  segmentId: string;
  snippet: string;
  fullText: string;
  speaker: string;
  timestamp: string;
  startTime?: number;
  endTime?: number;
}

export interface ClinicalSection {
  title: string;
  content: string;
  evidenceRefs: EvidenceReference[];
}

export interface StructuredMedicalNote {
  sections: {
    chiefComplaint: ClinicalSection;
    hpi: ClinicalSection;
    ros: ClinicalSection;
    physicalExam: ClinicalSection;
    assessment: ClinicalSection;
    plan: ClinicalSection;
    medications: ClinicalSection;
    allergies: ClinicalSection;
    problems: ClinicalSection;
    procedures: ClinicalSection;
  };
  metadata: {
    generatedAt: Date;
    generatedBy: string;
    templateName: string;
    patient: {
      name: string;
      mrn: string;
      age: string;
      dob: string;
    };
    appointment: {
      date: string;
      type: string;
      duration?: string;
    };
  };
  warnings: string[];
  evidenceMap: Record<string, EvidenceReference>;
}

export interface NotePreview {
  human: {
    format: string;
    content: StructuredMedicalNote; // Direct structured object
  };
  fhir: {
    version: string;
    bundle: any;
  };
  hl7v2: {
    version: string;
    message: string;
  };
  warnings: string[];
}

/**
 * Medical Note Types for Clinical Summary-based Documentation
 *
 * Professional medical documentation generated from clinical summary sections
 */

export interface MedicalNoteSection {
  title: string;
  content: string;
  status: 'captured' | 'not_captured';
  confidence: number;
  evidenceSegments: string[];
  physicianNotes?: string;
}

export interface MedicalNote {
  chiefComplaint: MedicalNoteSection;
  historyOfPresentIllness: MedicalNoteSection;
  pastMedicalHistory: MedicalNoteSection;
  familyHistory: MedicalNoteSection;
  socialHistory: MedicalNoteSection;
  psychiatricHistory: MedicalNoteSection;
  medications: MedicalNoteSection;
  reviewOfSystems: MedicalNoteSection;
  physicalExam: MedicalNoteSection;
  treatment: MedicalNoteSection;
  assessment: MedicalNoteSection;
  plan: MedicalNoteSection;
}

export interface MedicalNoteMetadata {
  generatedAt: string;
  overallConfidence: number;
  status?: 'draft' | 'pending' | 'signed';
  signedAt?: string;
  signedBy?: string;
}

/**
 * Display labels for medical note sections
 */
export const MEDICAL_NOTE_SECTION_LABELS: Record<keyof MedicalNote, string> = {
  chiefComplaint: 'Chief Complaint',
  historyOfPresentIllness: 'History of Present Illness',
  pastMedicalHistory: 'Past Medical History',
  familyHistory: 'Family History',
  socialHistory: 'Social History',
  psychiatricHistory: 'Psychiatric History',
  medications: 'Medications and Allergies',
  reviewOfSystems: 'Review of Systems',
  physicalExam: 'Physical Exam',
  treatment: 'Treatment',
  assessment: 'Assessment',
  plan: 'Plan'
};

/**
 * Color coding for medical note sections (for UI consistency)
 */
export const MEDICAL_NOTE_SECTION_COLORS: Record<keyof MedicalNote, 'blue' | 'green' | 'orange' | 'purple' | 'pink' | 'yellow' | 'teal' | 'indigo'> = {
  chiefComplaint: 'blue',
  historyOfPresentIllness: 'green',
  pastMedicalHistory: 'orange',
  familyHistory: 'pink',
  socialHistory: 'yellow',
  psychiatricHistory: 'indigo',
  medications: 'purple',
  reviewOfSystems: 'teal',
  physicalExam: 'green',
  treatment: 'blue',
  assessment: 'orange',
  plan: 'green'
};

/**
 * Section order for consistent rendering
 */
export const MEDICAL_NOTE_SECTION_ORDER: (keyof MedicalNote)[] = [
  'chiefComplaint',
  'historyOfPresentIllness',
  'pastMedicalHistory',
  'familyHistory',
  'socialHistory',
  'psychiatricHistory',
  'medications',
  'reviewOfSystems',
  'physicalExam',
  'treatment',
  'assessment',
  'plan'
];